import React, { useState, useEffect } from 'react';
import { Check, Loader2 } from 'lucide-react';
import apiService from './apiService';

const Settings = () => {
  const [settings, setSettings] = useState({
    // Notifications
    emailNotifications: true,
    pushNotifications: false,
    weeklyDigest: true,
    projectUpdates: true,
    taskReminders: true,
    mentionAlerts: true,

    // Privacy
    profileVisibility: 'team',
    showEmail: false,
    showActivity: true,

    // Preferences
    language: 'en',
    timezone: 'UTC-5',
    dateFormat: 'MM/DD/YYYY',
    autoSave: true,
    compactMode: false,

    // Integrations
    slackIntegration: false,
    googleCalendar: true,
    jiraSync: false
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiService.getSettings();
        if (response.settings) {
          // Map backend settings to component state
          const s = response.settings;
          setSettings({
            emailNotifications: s.notifications?.email ?? true,
            pushNotifications: s.notifications?.push ?? false,
            weeklyDigest: s.notifications?.weekly ?? false,
            projectUpdates: s.notifications?.updates ?? true,
            taskReminders: s.notifications?.reminders ?? true,
            mentionAlerts: s.notifications?.mentions ?? true,
            profileVisibility: s.privacy?.profileVisibility ?? 'team',
            showEmail: s.privacy?.showEmail ?? false,
            showActivity: s.privacy?.activityStatus ?? true,
            language: s.preferences?.language ?? 'en',
            timezone: s.preferences?.timezone ?? 'UTC',
            dateFormat: s.preferences?.dateFormat ?? 'MM/DD/YYYY',
            autoSave: true,
            compactMode: s.preferences?.compactMode ?? false,
            slackIntegration: s.integrations?.slack ?? false,
            googleCalendar: s.integrations?.googleCalendar ?? false,
            jiraSync: s.integrations?.jira ?? false
          });
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaved(false);
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Map component state back to backend format
      await apiService.updateSettings({
        notifications: {
          email: settings.emailNotifications,
          push: settings.pushNotifications,
          weekly: settings.weeklyDigest,
          updates: settings.projectUpdates,
          reminders: settings.taskReminders,
          mentions: settings.mentionAlerts
        },
        privacy: {
          profileVisibility: settings.profileVisibility,
          showEmail: settings.showEmail,
          activityStatus: settings.showActivity
        },
        preferences: {
          language: settings.language,
          timezone: settings.timezone,
          dateFormat: settings.dateFormat,
          compactMode: settings.compactMode
        },
        integrations: {
          slack: settings.slackIntegration,
          googleCalendar: settings.googleCalendar,
          jira: settings.jiraSync
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="aut-settings" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} className="aut-spinner" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '16px', color: 'var(--aut-text-muted)' }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  const Toggle = ({ checked, onChange }) => (
    <label className="aut-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="aut-toggle-slider"></span>
    </label>
  );

  return (
    <div className="aut-settings">
      <div className="aut-page-header">
        <h1 className="aut-page-title">Settings</h1>
        <p className="aut-page-description">Manage your account settings and preferences.</p>
      </div>

      {saved && (
        <div className="aut-alert aut-alert-success" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={18} />
          <span>Settings saved successfully.</span>
        </div>
      )}

      {/* Notifications */}
      <div className="aut-settings-group">
        <h3 className="aut-settings-group-title">Notifications</h3>
        <div className="aut-settings-list">
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Email Notifications</h4>
              <p className="aut-settings-item-description">Receive notifications via email</p>
            </div>
            <Toggle checked={settings.emailNotifications} onChange={() => handleToggle('emailNotifications')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Push Notifications</h4>
              <p className="aut-settings-item-description">Receive push notifications in browser</p>
            </div>
            <Toggle checked={settings.pushNotifications} onChange={() => handleToggle('pushNotifications')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Weekly Digest</h4>
              <p className="aut-settings-item-description">Get a weekly summary of your activity</p>
            </div>
            <Toggle checked={settings.weeklyDigest} onChange={() => handleToggle('weeklyDigest')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Project Updates</h4>
              <p className="aut-settings-item-description">Get notified when projects are updated</p>
            </div>
            <Toggle checked={settings.projectUpdates} onChange={() => handleToggle('projectUpdates')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Task Reminders</h4>
              <p className="aut-settings-item-description">Receive reminders for upcoming task deadlines</p>
            </div>
            <Toggle checked={settings.taskReminders} onChange={() => handleToggle('taskReminders')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Mention Alerts</h4>
              <p className="aut-settings-item-description">Get notified when someone mentions you</p>
            </div>
            <Toggle checked={settings.mentionAlerts} onChange={() => handleToggle('mentionAlerts')} />
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className="aut-settings-group">
        <h3 className="aut-settings-group-title">Privacy</h3>
        <div className="aut-settings-list">
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Profile Visibility</h4>
              <p className="aut-settings-item-description">Control who can see your profile</p>
            </div>
            <select
              className="aut-select"
              style={{ width: 'auto', minWidth: '140px' }}
              value={settings.profileVisibility}
              onChange={(e) => handleChange('profileVisibility', e.target.value)}
            >
              <option value="public">Public</option>
              <option value="team">Team Only</option>
              <option value="private">Private</option>
            </select>
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Show Email Address</h4>
              <p className="aut-settings-item-description">Display your email on your profile</p>
            </div>
            <Toggle checked={settings.showEmail} onChange={() => handleToggle('showEmail')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Show Activity Status</h4>
              <p className="aut-settings-item-description">Let others see when you're online</p>
            </div>
            <Toggle checked={settings.showActivity} onChange={() => handleToggle('showActivity')} />
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="aut-settings-group">
        <h3 className="aut-settings-group-title">Preferences</h3>
        <div className="aut-settings-list">
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Language</h4>
              <p className="aut-settings-item-description">Select your preferred language</p>
            </div>
            <select
              className="aut-select"
              style={{ width: 'auto', minWidth: '140px' }}
              value={settings.language}
              onChange={(e) => handleChange('language', e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="pt">Portuguese</option>
            </select>
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Timezone</h4>
              <p className="aut-settings-item-description">Set your local timezone</p>
            </div>
            <select
              className="aut-select"
              style={{ width: 'auto', minWidth: '180px' }}
              value={settings.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
            >
              <option value="UTC-8">Pacific Time (UTC-8)</option>
              <option value="UTC-7">Mountain Time (UTC-7)</option>
              <option value="UTC-6">Central Time (UTC-6)</option>
              <option value="UTC-5">Eastern Time (UTC-5)</option>
              <option value="UTC+0">UTC</option>
              <option value="UTC+1">Central European (UTC+1)</option>
            </select>
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Date Format</h4>
              <p className="aut-settings-item-description">Choose how dates are displayed</p>
            </div>
            <select
              className="aut-select"
              style={{ width: 'auto', minWidth: '140px' }}
              value={settings.dateFormat}
              onChange={(e) => handleChange('dateFormat', e.target.value)}
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Auto-save</h4>
              <p className="aut-settings-item-description">Automatically save changes as you type</p>
            </div>
            <Toggle checked={settings.autoSave} onChange={() => handleToggle('autoSave')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Compact Mode</h4>
              <p className="aut-settings-item-description">Use condensed layouts to show more content</p>
            </div>
            <Toggle checked={settings.compactMode} onChange={() => handleToggle('compactMode')} />
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="aut-settings-group">
        <h3 className="aut-settings-group-title">Integrations</h3>
        <div className="aut-settings-list">
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Slack Integration</h4>
              <p className="aut-settings-item-description">Connect with Slack for notifications</p>
            </div>
            <Toggle checked={settings.slackIntegration} onChange={() => handleToggle('slackIntegration')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Google Calendar</h4>
              <p className="aut-settings-item-description">Sync deadlines with Google Calendar</p>
            </div>
            <Toggle checked={settings.googleCalendar} onChange={() => handleToggle('googleCalendar')} />
          </div>
          <div className="aut-settings-item">
            <div className="aut-settings-item-info">
              <h4 className="aut-settings-item-title">Jira Sync</h4>
              <p className="aut-settings-item-description">Two-way sync with Jira issues</p>
            </div>
            <Toggle checked={settings.jiraSync} onChange={() => handleToggle('jiraSync')} />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
        <button className="aut-btn aut-btn-primary aut-btn-lg" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default Settings;
